import { ReviewService } from '../../services/ReviewService'
import { Employee, EmployeeDetails, EmployeeService } from '../../services/EmployeeService'
import { Review } from '../../types/definitions/review'
import { useCallback, useEffect, useState } from 'react'
import history from '../../utils/history'
import { NetworkConstants } from '../../utils/NetworkConstants'
import { createPathWithLang } from '../../utils/languagePathUtils'
import { useSelector } from 'react-redux'
import { RootState } from '../..'
import { Form } from '../../types/definitions/form'
import { FormService } from '../../services/FormService'
import { trackEvent, TrackingCategoryEnum, TrackingCategoryEventEnum } from '../../services/EventTrackingService'
import ErrorModal from '../molecules/ErrorModal'
import WarningIcon from '../atoms/Icons/WarningIcon.svg'
import TechProfileCard from '../organisms/TechProfile/TechProfileCard'
import TechProfileStartCard from '../organisms/TechProfile/TechProfileStartCard'
import Spinner from '../atoms/Spinner'

const EMAIL = 'mailto:grapes@theagilemonkeys.com'

const redirectToNotFound = (): void => {
  history.push(createPathWithLang(NetworkConstants.URL_NOT_FOUND))
}

export const TechProfile = (): JSX.Element => {
  const currentUser = useSelector<RootState, EmployeeDetails | null>(state => state.auth.user)
  const [employeeId] = useState<number | undefined>(currentUser?.id)
  const [employee, setEmployee] = useState<Employee>()
  const [selfForm, setSelfForm] = useState<Form>()
  const [selfEvaluation, setSelfEvaluation] = useState<Review>()
  const [showErrorModal, setShowErrorModal] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(true)

  const requestSelfReview = async (employeeId: number) => {
    try {
      if (employeeId) {
        const requestedForms = await FormService.findAll()
        const techBrief = await ReviewService.findSelfReviewByEmployeeId(employeeId)
        const selfForm = requestedForms.find(form => form.id === techBrief.form.id)
        setSelfEvaluation(techBrief)
        setSelfForm(selfForm)
      }
    } catch (error) {
      console.error('Request service error: ' + error)
    }
  }

  useEffect(() => {
    const requestInfoEmployee = async () => {
      setIsLoading(true)
      const employee = await EmployeeService.findOne(Number(employeeId))
      setEmployee(employee as unknown as Employee)
      await requestSelfReview(employee.id)
      setIsLoading(false)
    }

    requestInfoEmployee().catch(error => {
      console.error('Employee service error: ' + error.message)
      redirectToNotFound()
    })
  }, [employeeId])

  useEffect(() => {
    trackEvent(TrackingCategoryEnum.INDIVIUAL_REPORT, TrackingCategoryEventEnum.INDIVIUAL_REPORT.PAGE_ON_LOAD, '')
  }, [])

  const goToTeachBrief = (): void => {
    if (employee) {
      history.push(createPathWithLang(NetworkConstants.URL_TECH_EVALUATION))
    }
  }

  const handleTechBriefUpdateButton = () => {
    trackEvent(
      TrackingCategoryEnum.TECH_EVALUATION,
      TrackingCategoryEventEnum.TECH_EVALUATION.CLICK_UPDATE_BTN,
      JSON.stringify({
        reviewId: selfEvaluation?.id,
      })
    )
    history.push(createPathWithLang(NetworkConstants.URL_TECH_EVALUATION))
  }

  const handlePublishReview = async (): Promise<void> => {
    try {
      if (selfEvaluation) await ReviewService.finishReview(selfEvaluation.id)
      history.push(createPathWithLang(NetworkConstants.URL_TECH_PROFILE))
    } catch (error) {
      setShowErrorModal(true)
      console.error(error)
    } finally {
      employee?.id && requestSelfReview(employee?.id)
    }
  }

  if (isLoading) return <Spinner />

  return (
    <div className="container mx-auto">
      <div className="employee-results">
        {employee && (
          <div className="employee-results__content">
            <div className="mb-16">
              <h1 className="text-6xl text-dark-blue font-semibold mb-3 mt-5">{employee.name}</h1>
              <span className="text-3xl text-gray block mt-8">This is where you can update your profile</span>
            </div>
            {!isLoading && (
              <div>
                {selfEvaluation ? (
                  <TechProfileCard
                    reviewTechProfile={selfEvaluation}
                    selfForm={selfForm || {}}
                    onUpdateReview={handleTechBriefUpdateButton}
                    onPublishReview={handlePublishReview}
                  />
                ) : (
                  <TechProfileStartCard onStart={goToTeachBrief} />
                )}
              </div>
            )}
          </div>
        )}
      </div>
      {showErrorModal && (
        <ErrorModal
          icon={<WarningIcon />}
          visible={showErrorModal}
          message={
            <p>
              <span className={`modal-title`}>Something went wrong while saving your answers.</span>
              <br />
              <span className="text-grey">
                <b>Try reloading the page and save again.</b> If the problem persists,{' '}
                <a href={EMAIL}>contact support</a>.
              </span>
            </p>
          }
          onClose={() => setShowErrorModal(false)}
        />
      )}
    </div>
  )
}
